import React, { ForwardRefRenderFunction, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { sortTablesByUpperCaseFirst } from 'constants/global';
import {
  AGGREGATE_FUNCTION_TYPE_MAP,
  AggregateFunctionType,
  AggregateItem,
  FilterItem,
  getAggregateFunctionTypeString,
  REPORT_FILTER_SECTIONS,
  ReportFilterSectionId,
  SortDirection,
  typeStringMap,
  VisualizationFormState,
} from 'constants/reports-templates';
import { ReportingColumn } from 'interfaces/report-configuration.interface';
import {
  FilterReportTemplates,
  JoinRow,
  ReportTemplateConfiguration,
  SortingRule,
  VisualizationItemPayload,
} from 'interfaces/report-templates.interface';
import { Modal, SelectFormInput } from 'shared-components';

import CreateReportTemplateAggregateModal from './CreateReportTemplateAggregateModal';
import CreateReportTemplateFilterItem from './CreateReportTemplateFilterItem';
import CreateReportTemplateFiltersModal from './CreateReportTemplateFiltersModal';
import CreateReportTemplateGroupingModal from './CreateReportTemplateGroupingModal';
import CreateReportTemplateJoinModal from './CreateReportTemplateJoinModal';
import CreateReportTemplateSortByModal from './CreateReportTemplateSortByModal';
import CreateReportTemplateVisualizationBlock from './CreateReportTemplateVisualizationBlock';

type Props = {
  selectedColumnsData?: ReportingColumn[];
  onValidationChange?: (isValid: boolean) => void;
  onConfigChange?: () => void;
  initialVisualization?: VisualizationFormState | null;
  initialValues?: any;
};

export type ReportTemplateFiltersBlockHandle = {
  resetForm: () => void;
  getCurrentConfiguration: () => any;
  getSelectedColumnsData: () => ReportingColumn[];
  setInitialConfiguration: (data: ReportTemplateConfiguration) => void;
  getTemplateName: () => string;
};

const CreateReportTemplateFiltersBlock: ForwardRefRenderFunction<ReportTemplateFiltersBlockHandle, Props> = (
  { selectedColumnsData, onValidationChange, onConfigChange, initialVisualization, initialValues },
  ref
) => {
  const { t } = useTranslation();
  const [templateName, setTemplateName] = useState<string>('');
  const query = initialValues?.query ?? initialValues;
  const [count, setCount] = useState(0);

  type FormValues = {
    name: string;
    selectedTable: { label: string; value: string } | null;
    grouping: { label: string; value: string } | null;
    sorting: { label: string; value: string } | null;
    sortingDirection: { label: string; value: SortDirection } | null;
    filters: FilterItem[];
    visualization: VisualizationFormState | null;
  };

  const createReportTemplateMethods = useForm<FormValues>({
    defaultValues: {
      name: initialValues?.name ?? '',
      selectedTable: query?.tableName ? { label: query?.tableName, value: query?.tableName } : null,
      grouping: query?.groupBy?.name ? { label: query?.groupBy.name, value: query?.groupBy.name } : null,
      visualization: initialVisualization ?? null,
      sorting: query?.sorting?.[0]?.column?.name
        ? { label: query?.sorting[0].column.name, value: query?.sorting[0].column.name }
        : null,
      filters: query?.filters?.length
        ? query?.filters.map((filter: any, index: any) => ({
            id: String(index + 1),
            column: filter.columnName,
            condition: filter.condition,
            value: filter.value ?? '',
            locked: filter.locked ?? false,
            operator: index > 0 ? (filter.operator as FilterItem['operator']) : undefined,
          }))
        : [],
    },
  });
  const { control, reset, setValue, watch } = createReportTemplateMethods;

  const watchedTable = watch('selectedTable');
  const [isInitializing, setIsInitializing] = useState(false);
  const watchedSelectedTable = useWatch({
    control,
    name: 'selectedTable',
  });

  const selectedTable = watchedSelectedTable?.value ?? null;
  const [activeModal, setActiveModal] = useState<ReportFilterSectionId | null>(null);
  const [savedJoins, setSavedJoins] = useState<JoinRow[]>([]);
  const [grouping, setGrouping] = useState<VisualizationItemPayload | null>(query?.groupBy || null);
  const [, setJoinsDraft] = useState<JoinRow[]>([]);
  const [aggregates, setAggregates] = useState<{ func: AggregateFunctionType; column?: string }[]>([]);
  const [, setFilters] = useState<FilterItem[]>([]);
  const [, setAggregatesDraft] = useState<AggregateItem[]>([]);
  const [visualization, setVisualization] = useState<VisualizationFormState | null>(null);
  const [, setSelectedVisualization] = useState<string>('');
  const [, setSelectedColumn] = useState<string>('');
  const [, setSelectedColumns] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const idCounter = useRef(0);

  const getNextId = () => {
    idCounter.current += 1;
    return idCounter.current;
  };
  const [savedAggregates, setSavedAggregates] = useState<AggregateItem[]>([]);
  const [savedFilters, setSavedFilters] = useState<FilterItem[]>([]);

  const adaptSorting = (
    sorting?: SortingRule[] | null,
    table?: string
  ): { table: string; column: string; direction: SortDirection } | null => {
    if (!sorting?.[0] || !table) {
      return null;
    }

    const sort = sorting[0];

    return {
      table,
      column: sort.column.name,
      direction: sort.direction.toString() as SortDirection,
    };
  };

  const [sorting, setSorting] = useState<{
    table: string;
    column: string;
    direction: SortDirection;
  } | null>(adaptSorting(query?.sorting, query?.tableName));

  const uniqueTableOptions = useMemo(() => {
    if (!selectedColumnsData) {
      return [];
    }

    const uniqueTables = Array.from(new Set(selectedColumnsData.map((col) => col.tableName))).map((name) => ({ name }));
    const sortedTables = sortTablesByUpperCaseFirst(uniqueTables);

    return sortedTables.map((table) => ({
      label: table.name,
      value: table.name,
    }));
  }, [selectedColumnsData]);

  const getSectionLabel = (id: ReportFilterSectionId | null) =>
    REPORT_FILTER_SECTIONS.find((section) => section.id === id)?.label ?? '';

  const onSubmitGrouping = (column: string) => {
    if (!column || !selectedTable) {
      return;
    }

    const columnMeta = selectedColumnsData?.find((col) => col.name === column && col.tableName === selectedTable);
    if (!columnMeta) {
      return;
    }

    setGrouping({
      name: columnMeta.name,
      displayName: columnMeta.name,
      tableName: columnMeta.tableName,
      type: columnMeta.columnType,
      displayType: typeStringMap[columnMeta.columnType],
    });

    setActiveModal(null);
  };

  const onSubmitAggregates = (newAggregates: { func: AggregateFunctionType; column?: string }[]) => {
    const aggregatesWithId: AggregateItem[] = newAggregates.map((agg) => ({
      id: getNextId(),
      func: agg.func,
      column: agg.column ?? null,
    }));
    setSavedAggregates(aggregatesWithId);
    setAggregates(newAggregates);
    setActiveModal(null);
  };

  const onSubmitFilters = (newFilters: Omit<FilterItem, 'id'>[]) => {
    const filtersWithId: FilterItem[] = newFilters.map((filter, index) => ({
      id: String(getNextId()),
      column: filter.column,
      condition: filter.condition,
      value: filter.value ?? '',
      locked: filter.locked ?? false,
      operator: index > 0 ? filter.operator : undefined,
    }));

    setSavedFilters(filtersWithId);
    setFilters(filtersWithId);
    setActiveModal(null);
  };

  const onSubmitSortBy = (column: string, direction: SortDirection) => {
    if (!column || !selectedTable) {
      return;
    }

    setSorting({ table: selectedTable, column, direction });
    setActiveModal(null);
  };

  const clearForm = () => {
    setActiveModal(null);
    setSavedJoins([]);
    setGrouping(null);
    setSorting(null);
    setAggregates([]);
    setSavedAggregates([]);
    setSavedFilters([]);
    setFilters([]);
    setVisualization(null);
    setSelectedVisualization('');
    setSelectedColumn('');
    setSelectedColumns([]);
  };

  const resetForm = () => {
    clearForm();
    reset({
      selectedTable: null,
    });
  };

  const getCurrentConfiguration = () => {
    if (!selectedTable || !visualization) {
      return null;
    }

    const payload: Record<string, any> = {
      tableName: selectedTable,
      visualization,
    };

    const query: any = {
      tableName: selectedTable,
      joins: savedJoins.length ? savedJoins : [],
      filters: savedFilters.length ? savedFilters : [],
    };

    if (savedAggregates.length) {
      query.displayColumns = savedAggregates.map((agg) => {
        const isCount = agg.func === 'Count';
        const columnName = isCount ? 'Count' : agg.column;
        const functionTypeNumber = agg.func ? AGGREGATE_FUNCTION_TYPE_MAP[agg.func] : 3;
        const aggregateId = isCount
          ? `${selectedTable}.Count.${functionTypeNumber}`
          : `${selectedTable}.${agg.column}.${functionTypeNumber}`;

        return {
          id: aggregateId,
          customName: null,
          aggregateFunctionType: functionTypeNumber,
          displayName: isCount ? 'Count' : `${agg.func} ${agg.column}`,
          tableName: selectedTable,
          name: columnName,
          type: 3,
          displayType: 'BIGINT',
        };
      });

      payload.aggregates = savedAggregates.map((agg) => ({
        func: agg.func,
        column: agg.func === 'Count' ? 'Count' : agg.column,
      }));
    } else {
      query.displayColumns = [];
    }

    payload.query = query;

    if (savedJoins.length) payload.joins = savedJoins;
    if (grouping) payload.groupBy = [grouping];
    if (savedFilters.length) payload.filters = savedFilters;
    if (sorting) payload.sorting = [sorting];

    if (visualization.type !== 'Table' && 'timelapseType' in visualization) {
      payload.timelapseType = visualization.timelapseType;
    }

    return payload;
  };

  useEffect(() => {
    setVisualization((prev) => {
      if (!prev || prev.type === 'Table') {
        return prev;
      }

      return {
        ...prev,
        column: '',
      };
    });
  }, [grouping, savedAggregates, savedFilters, sorting, savedJoins, isInitializing]);

  const modalContentMap: Record<ReportFilterSectionId, React.ReactNode> = {
    [ReportFilterSectionId.JOIN]: (
      <CreateReportTemplateJoinModal
        selectedColumnsData={selectedColumnsData}
        selectedTable={selectedTable}
        onCloseModal={() => setActiveModal(null)}
        onSubmit={(joins) => {
          setSavedJoins(joins);
          setActiveModal(null);
        }}
        selectedJoins={savedJoins}
      />
    ),
    [ReportFilterSectionId.GROUPING]: (
      <CreateReportTemplateGroupingModal
        control={control}
        selectedColumnsData={selectedColumnsData ?? []}
        onCloseModal={() => setActiveModal(null)}
        onSubmit={onSubmitGrouping}
        selectedGroupingColumns={selectedColumnsData?.map((col) => col.name) ?? []}
        selectedGroupingColumnName={grouping?.name}
      />
    ),
    [ReportFilterSectionId.AGGREGATE]: (
      <CreateReportTemplateAggregateModal
        control={control}
        selectedColumnsData={selectedColumnsData}
        selectedTable={selectedTable}
        onClose={() => setActiveModal(null)}
        initialAggregates={aggregates}
        onSubmit={onSubmitAggregates}
      />
    ),
    [ReportFilterSectionId.FILTER]: (
      <CreateReportTemplateFiltersModal
        control={control}
        selectedColumnsData={selectedColumnsData}
        selectedTable={selectedTable}
        initialFilters={savedFilters.map((filter) => {
          const col = filter.column as any;
          return {
            ...filter,
            column: typeof col === 'object' ? col.name : col,
            displayName: typeof col === 'object' ? col.displayName : col,
          };
        })}
        onCloseModal={() => setActiveModal(null)}
        onSubmit={onSubmitFilters}
      />
    ),
    [ReportFilterSectionId.SORT]: (
      <CreateReportTemplateSortByModal
        control={control}
        selectedColumnsData={selectedColumnsData ?? []}
        selectedTable={selectedTable}
        onCloseModal={() => setActiveModal(null)}
        onSubmit={onSubmitSortBy}
        savedAggregates={savedAggregates}
        initialSorting={sorting ?? undefined}
      />
    ),
  };

  const handleFilterItemClick = (id: ReportFilterSectionId) => {
    if (id === ReportFilterSectionId.JOIN) {
      setJoinsDraft([...savedJoins]);
    } else if (id === ReportFilterSectionId.AGGREGATE) {
      setAggregatesDraft(
        savedAggregates.map((agg) => ({
          id: getNextId(),
          func: agg.func,
          column: agg.column,
        }))
      );
    }
    setActiveModal(id);
  };

  useImperativeHandle(ref, () => ({
    resetForm,
    getCurrentConfiguration,
    getSelectedColumnsData: () => selectedColumnsData ?? [],
    getTemplateName: () => templateName,
    setInitialConfiguration: (data: ReportTemplateConfiguration) => {
      setIsInitializing(true);

      if (data.tableName) {
        setValue('selectedTable', {
          label: data.tableName,
          value: data.tableName,
        });
      }

      if (data.visualization) {
        setVisualization(data.visualization);

        setSelectedVisualization(data.visualization.type);

        if (data.visualization.type === 'Table') {
          setSelectedColumns(data.visualization.columns ?? []);
        } else {
          setSelectedColumn(data.visualization.column ?? '');
        }
      }

      const sorting = data?.sorting ?? [];

      if (sorting.length > 0) {
        const sort = sorting[0];
        setSorting({
          table: sort.column.tableName,
          column: sort.column.name,
          direction: sort.direction === 1 ? 'asc' : 'desc',
        });

        setValue('sorting', {
          label: sort.column.name,
          value: sort.column.name,
        });

        setValue('sortingDirection', {
          label: sort.direction === 1 ? 'Ascending' : 'Descending',
          value: sort.direction === 1 ? 'asc' : 'desc',
        });
      }

      const convertApiJoinToJoinRow = (apiJoin: any): JoinRow => ({
        id: apiJoin.id || Date.now(),
        leftTable: apiJoin.column?.tableName || '',
        rightTable: apiJoin.joinColumn?.tableName || '',
        leftColumn: apiJoin.column?.name || '',
        rightColumn: apiJoin.joinColumn?.name || '',
        leftDisplayType: apiJoin.column?.displayType,
      });

      if (Array.isArray(data?.joins) && data.joins.length > 0) {
        setSavedJoins(data.joins);
      } else if (data?.query && Array.isArray(data.query.joins) && data.query.joins.length > 0) {
        const convertedJoins = data.query.joins.map(convertApiJoinToJoinRow);
        setSavedJoins(convertedJoins);
      }

      const filtersArray = (data as any)?.filters || (data as any)?.query?.filters;

      if (Array.isArray(filtersArray) && filtersArray.length > 0) {
        type ApiFilter = FilterReportTemplates & {
          column?: { name: string; tableName: string };
          isLocked?: boolean;
        };

        const adapted: FilterItem[] = filtersArray.map((filter, index) => {
          const apiFilter = filter as ApiFilter;
          return {
            id: apiFilter.id || String(getNextId()),
            column: apiFilter.column?.name || apiFilter.columnName,
            condition: apiFilter.condition,
            value: apiFilter.value ?? '',
            locked: apiFilter.isLocked ?? apiFilter.locked ?? false,
            operator: index > 0 ? (apiFilter.operator as 'And' | 'Or' | undefined) : undefined,
          };
        });

        setSavedFilters(adapted);
        setFilters(adapted);
      } else {
      }

      const aggregatesArray = data?.aggregates || data?.query?.aggregates;

      if (Array.isArray(aggregatesArray) && aggregatesArray.length > 0) {
        const adaptedAggregates = aggregatesArray.map((agg) => ({
          func: agg.func as AggregateFunctionType,
          column: agg.func === 'Count' || agg.column === 'Count' ? undefined : agg.column,
        }));
        setSavedAggregates(
          aggregatesArray.map((agg) => ({
            id: getNextId(),
            func: agg.func as AggregateFunctionType,
            column: agg.func === 'Count' || agg.column === 'Count' ? null : agg.column || null,
          }))
        );
        setAggregates(adaptedAggregates);
      } else {
        const displayColumns = data?.query?.displayColumns;

        if (Array.isArray(displayColumns)) {
          const foundAggregates = displayColumns
            .filter((col) => {
              const hasAggregateType = col.aggregateFunctionType && col.aggregateFunctionType !== null;
              const hasAggregateDisplay =
                col.displayName &&
                (col.displayName.includes('Sum of') ||
                  col.displayName.includes('Count') ||
                  col.displayName.includes('Average of') ||
                  col.displayName.includes('Maximum of') ||
                  col.displayName.includes('Minimum of'));

              const isCalculatedAggregate =
                col.displayType === 'BIGINT' || (col.displayName && col.name && col.displayName !== col.name);

              return hasAggregateType || hasAggregateDisplay || isCalculatedAggregate;
            })
            .map((col) => {
              let func = col.aggregateFunctionType ? getAggregateFunctionTypeString(col.aggregateFunctionType) : null;

              if (!func && col.displayName) {
                if (col.displayName.includes('Sum of')) func = 'Sum of';
                else if (col.displayName.includes('Count')) func = 'Count';
                else if (col.displayName.includes('Distinct count of')) func = 'Distinct count of';
                else if (col.displayName.includes('Average of')) func = 'Average of';
                else if (col.displayName.includes('Maximum of')) func = 'Maximum of';
                else if (col.displayName.includes('Minimum of')) func = 'Minimum of';
              }

              return {
                func: func || 'Sum of',
                column: func === 'Count' || col.displayName === 'Count' ? 'Count' : col.name,
              };
            });

          if (foundAggregates.length > 0) {
            const adaptedForState = foundAggregates.map((agg) => ({
              func: agg.func as AggregateFunctionType,
              column: agg.func === 'Count' || agg.column === 'Count' ? undefined : agg.column,
            }));

            setSavedAggregates(
              foundAggregates.map((agg) => ({
                id: getNextId(),
                func: agg.func as AggregateFunctionType,
                column: agg.func === 'Count' || agg.column === 'Count' ? null : agg.column,
              }))
            );
            setAggregates(adaptedForState);
          }
        }
      }

      const groupByItem = data?.groupBy || data?.query?.groupBy?.[0];
      if (groupByItem) {
        setGrouping(groupByItem);
      }

      if (data.name) {
        setTemplateName(data.name);
      }

      setIsInitializing(false);
    },
  }));

  useEffect(() => {
    if (initialVisualization && !isInitialized) {
      if ('type' in initialVisualization) {
        setSelectedVisualization(initialVisualization.type);
      }

      if (initialVisualization.type === 'Table' && 'columns' in initialVisualization) {
        setSelectedColumns(initialVisualization.columns);
      } else if ('column' in initialVisualization && typeof initialVisualization.column === 'string') {
        setSelectedColumn(initialVisualization.column);
      }
      setVisualization(initialVisualization);
      setIsInitialized(true);
    }
  }, [initialVisualization, isInitialized]);

  useEffect(() => {
    const isVisualizationValid = (() => {
      if (!visualization) {
        return false;
      }

      if (visualization.type === 'Table') {
        return Array.isArray(visualization.columns) && visualization.columns.length > 0;
      }

      return typeof visualization.column === 'string' && visualization.column.trim() !== '';
    })();

    const isValid = Boolean(selectedTable && visualization?.type && isVisualizationValid);

    onValidationChange?.(isValid);
  }, [selectedTable, visualization]);

  return (
    <div className="w-[23.875rem] p-6 overflow-auto">
      <FormProvider {...createReportTemplateMethods}>
        <form
          onSubmit={createReportTemplateMethods.handleSubmit((data) => {
            console.log('Form submitted with:', data);
          })}
        >
          <SelectFormInput
            dataCy="expiring-subscription-select"
            control={control as any}
            onSelectValue={(value) => {
              const newValue = value?.value ?? null;
              if (newValue !== selectedTable) {
                clearForm();
                setVisualization(null);
                reset({
                  selectedTable: newValue ? { label: newValue, value: newValue } : null,
                  grouping: null,
                  sorting: null,
                  sortingDirection: null,
                  filters: [],
                  visualization: null,
                  name: watchedTable?.value === newValue ? watch('name') : '',
                });
                setCount((key) => key + 1);
              }
            }}
            name="selectedTable"
            options={uniqueTableOptions}
            label={t('SelectTable')}
            labelClassName="mb-2 text-black-900 dark:text-white-00"
            required
            placeholder={t('SelectTable')}
            isClearable
          />
          {selectedTable && (
            <div className="mt-4">
              {REPORT_FILTER_SECTIONS.map(({ id, label }) => (
                <CreateReportTemplateFilterItem
                  key={id}
                  title={label}
                  onClick={() => handleFilterItemClick(id as ReportFilterSectionId)}
                  joins={id === 'join' ? savedJoins : undefined}
                  grouping={
                    id === 'grouping' && grouping ? { table: grouping.tableName, column: grouping.name } : undefined
                  }
                  aggregates={
                    id === 'aggregate'
                      ? savedAggregates.map((agg) => {
                          return {
                            func: getAggregateFunctionTypeString(agg.func),
                            column: agg.column || undefined,
                          };
                        })
                      : undefined
                  }
                  filters={id === 'filter' ? savedFilters : undefined}
                  sorting={id === 'sort' ? sorting : undefined}
                  onDeleteJoin={(joinId: number) =>
                    setSavedJoins((prev) => prev.filter((joins) => joins.id !== joinId))
                  }
                  onDeleteGroupings={() => setGrouping(null)}
                  onDeleteAggregate={(index) => {
                    setSavedAggregates((prev) => prev.filter((_, i) => i !== index));
                    setAggregates((prev) => prev.filter((_, i) => i !== index));
                  }}
                  onDeleteFilter={(filterId: string) => {
                    setSavedFilters((prev) => {
                      const updated = prev.filter((filtered) => filtered.id !== filterId);
                      localStorage.setItem('filter', JSON.stringify(updated));
                      return updated;
                    });
                    setFilters((prev) => prev.filter((f) => f.id !== filterId));
                  }}
                  onDeleteSorting={() => setSorting(null)}
                />
              ))}

              <CreateReportTemplateVisualizationBlock
                key={count}
                control={control}
                selectedTable={selectedTable}
                selectedColumnsData={selectedColumnsData ?? []}
                onConfigChange={onConfigChange}
                onChange={setVisualization}
                groupingColumn={grouping ? [grouping.name] : []}
                aggregateColumns={savedAggregates
                  .filter((agg) => agg.func && (agg.func === 'Count' || agg.column))
                  .map((agg) => ({
                    label: agg.func === 'Count' ? 'Count' : `${agg.func} ${agg.column}`,
                    value: agg.func === 'Count' ? 'Count' : `${agg.func} ${agg.column}`,
                    rawColumn: agg.column ?? undefined,
                  }))}
                initialVisualization={initialVisualization}
                selectedVisualizationType={visualization?.type ?? null}
                selectedVisualizationColumn={(() => {
                  const column = visualization?.type !== 'Table' ? visualization?.column ?? '' : '';
                  return column;
                })()}
                selectedVisualizationColumns={visualization?.type === 'Table' ? visualization.columns : []}
                selectedTimelapseType={visualization?.type !== 'Table' ? visualization?.timelapseType : undefined}
              />
            </div>
          )}

          <Modal
            key={activeModal}
            bodyClassName="!w-auto"
            isOpen={!!activeModal}
            onClose={() => setActiveModal(null)}
            heading={t(getSectionLabel(activeModal))}
          >
            {activeModal && modalContentMap[activeModal]}
          </Modal>
        </form>
      </FormProvider>
    </div>
  );
};

export default React.forwardRef(CreateReportTemplateFiltersBlock);
